import { useAppDispatch } from "Components/Hooks/hooks";
import AlertModal from "Components/Modals/AlertModal";
import moment from "moment";
import React, { useState } from "react";
import {
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
} from "reactstrap";
import { updateInvoiceStatus } from "slices/thunks";
import { InvoiceDetailsPayload } from "types/invoice.api";
import { getStatusBadge } from "utils/getStatusBatch";

interface InvoiceBasicInfoProps {
  invoice: InvoiceDetailsPayload;
}

const InvoiceBasicInfo: React.FC<InvoiceBasicInfoProps> = ({ invoice }) => {
  const [openAlertModal, setOpenAlertModal] = useState(false);
  const disPatch = useAppDispatch();
  const validateDate = (dateString: string) => {
    return moment(dateString).format("YYYY-MM-DD");
  };
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const toggle = () => setDropdownOpen((prevState) => !prevState);

  const STATUS_GROUPS = [["paid"]];

  const changeStatus = (status: string) => {
    if (status === "paid") {
      setOpenAlertModal(true);
      return;
    }
    disPatch(updateInvoiceStatus({ status: status, invoice_id: invoice.id }));
  };

  return (
    <div className="d-flex flex-column p-3 bg-white br-10">
      {/* Title */}
      <div className="fw-medium fs-16 text-black2D">Basic Information</div>

      {/* Border */}
      <div className="border-bottom my-3 mx-n4"></div>

      {/* Table-like layout */}
      <div className="d-flex flex-column gap-3">
        {/* Row: Quote Ref */}
        <div className="d-flex align-items-center">
          <div
            className="text-grey2D fw-normal fs-14 font-primary"
            style={{ width: "120px" }}
          >
            Quote Ref.
          </div>
          <div className="text-grey89 fw-normal fs-14 font-primary">
            {invoice.quote?.reference_number || ""}
          </div>
        </div>

        {/* Row: Created On */}
        <div className="d-flex align-items-center">
          <div
            className="text-black2D fw-normal fs-14 font-primary"
            style={{ width: "120px" }}
          >
            Created On
          </div>
          <div className="text-grey89 fw-normal fs-14 font-primary">
            {validateDate(invoice.created_at)}
          </div>
        </div>

        {/* Row: Due Date */}
        <div className="d-flex align-items-center">
          <div
            className="text-black2D fw-normal fs-14 font-primary"
            style={{ width: "120px" }}
          >
            Due Date
          </div>
          <div className="text-grey89 fw-normal fs-14 font-primary">
            {validateDate(invoice.due_date)}
          </div>
        </div>

        {/* Row: Status */}
        <div className="d-flex align-items-center">
          <div
            className="text-black2D fw-normal fs-14 font-primary"
            style={{ width: "120px" }}
          >
            Status
          </div>
          <Dropdown isOpen={dropdownOpen} toggle={toggle}>
            <DropdownToggle tag="div" className="cursor-pointer">
              {getStatusBadge(invoice.status, true)}
            </DropdownToggle>
            <DropdownMenu>
              {STATUS_GROUPS.flat().map((status) => (
                <DropdownItem
                  key={status}
                  onClick={() => changeStatus(status)}
                  active={invoice.status === status}
                  className="text-capitalize"
                  disabled={invoice.status === status}
                >
                  {status}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>
      <AlertModal
        title="Are you sure you want to mark this invoice as paid?"
        description="Please confirm that you have received the payment from the client before marking it as paid."
        isOpen={openAlertModal}
        onConfirm={() => {
          setOpenAlertModal(false);
          disPatch(
            updateInvoiceStatus({ status: "paid", invoice_id: invoice.id }),
          );
        }}
        onCancel={() => {
          setOpenAlertModal(false);
        }}
      />
    </div>
  );
};

export default InvoiceBasicInfo;
